import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";

import { LogService } from "@/services";
import store, { ApplicationModule, getStoreModule } from "@/store";
import { SearchItem, User, VignetteEndpoints, VignetteModules } from "@/types";

/**
 * this.context available for normal VueX style access and operations.
 */
@Module({ namespaced: true, name: "session" })
export class SessionModule extends VuexModule {
  _user: User = null;
  _hasVignetteAccess = false;

  //***** GETTERS *****//

  get getUser(): User {
    return this._user;
  }

  get isLoggedIn(): boolean {
    return this._user != null;
  }

  /**
   * If true, implies isLoggedIn === true && this.user != null
   */
  // TODO: this should not include active members
  get isTrialUser(): boolean {
    return this._user != null && this._user.isVignetteTrial;
  }

  /**
   * If true, implies isLoggedIn === true
   */
  // TODO: this should not include active members
  get isTrialActive(): boolean {
    return this._user != null && this.isLoggedIn && this._user.isVignetteTrialActive;
  }

  /**
   * If true, implies isLoggedIn === true
   */
  get isWebUser(): boolean {
    return this._user != null && this.isLoggedIn && this._user.userType === "web_user";
  }

  /**
   * If true, implies isLoggedIn === true
   */
  get isIpUser(): boolean {
    return this._user != null && this.isLoggedIn && this._user.userType === "ip_user";
  }

  /**
   * If true, implies isLoggedIn === true
   */
  get isVignetteOnlyUser(): boolean {
    return this._user != null && this.isLoggedIn && this._user.isVignetteOnly;
  }

  /**
   * If true, implies isLoggedIn === true
   */
  get isStaffOrSuperUser(): boolean {
    return this._user != null && this.isLoggedIn && (this._user.isStaff || this._user.isSuperUser);
  }

  /**
   * If true, implies isLoggedIn === true && this.user != null
   */
  get hasVignetteAccess(): boolean {
    return this._user != null && this.isLoggedIn && this._hasVignetteAccess;
  }

  /**
   * If true, implies isLoggedIn === true && this.user != null
   */
  get hasVignetteModuleAccess(): (module: VignetteModules) => boolean {
    return (module: VignetteModules): boolean => {
      return this._user != null && this.hasVignetteAccess && this._user.subscriptionModules.includes(module);
    };
  }

  /**
   * If true, implies isLoggedIn === true && this.user != null
   */
  get hasLapsedSubscription(): boolean {
    // return this._user != null && !this.hasVignetteAccess && !!this._user.chargifyCustomerId;
    // TODO: validate this change before trial full launch
    return this._user != null && !this.hasVignetteAccess && this._user.accountExpiration < new Date();
  }

  /**
   * If true, implies isLoggedIn === true && this.user != null
   */
  get isHidePayWalledProfilesPreference(): boolean {
    return this._user != null && this.isLoggedIn && this._user.hidePayWalledProfilesPreference;
  }

  /**
   * This is the list of profile Ids for a trial user has viewed.
   */
  get getProfilesViewedDuringTrial(): number[] {
    if (this._user) {
      return this._user.profilesViewedDuringTrial;
    }
    return [];
  }

  /**
   * Returns true if provided profile id is in user's `Your Viewed Profiles` list
   */
  get hasViewedProfileDuringTrial(): (profileId: number) => boolean {
    return (profileId: number): boolean => {
      return this._user != null && this._user.profilesViewedDuringTrial.indexOf(profileId) !== -1;
    };
  }

  get hasReachedTrialLimit(): boolean {
    return (
      this._user != null &&
      this._user.profilesViewedDuringTrial.length >= getStoreModule(ApplicationModule, store).getTrialProfileLimit
    );
  }

  get hasCustomLogo(): boolean {
    return this._user != null && this._user.accountOrganization.hasCustomLogo;
  }

  //***** MUTATIONS - SYNCHRONOUS *****//

  // DEV REMINDERS:
  // Mutation functions MUST NOT be async functions.
  // Also do not define Mutation & Actions as arrow️ functions, since we need to rebind them at runtime.

  @Mutation
  public setUser(user: User): void {
    this._user = user;
  }

  @Mutation
  public setProfilesViewedDuringTrial(profileId: number): void {
    if (this._user != null) {
      if (
        this._user.profilesViewedDuringTrial.indexOf(profileId) === -1 &&
        this._user.profilesViewedDuringTrial.length < getStoreModule(ApplicationModule, store).getTrialProfileLimit
      ) {
        this._user.profilesViewedDuringTrial.push(profileId);
      }
    } else {
      // Do I need to do anything here, I don't know
    }
  }

  /**
   * This is the list of profile search items for a trial user has viewed.
   */
  get profilesViewedDuringTrialItems(): SearchItem[] {
    if (this._user) {
      return this._user.profilesViewedDuringTrialItems;
    }
    return [];
  }

  @Mutation
  public setProfilesViewedDuringTrialItems(profilesItems: SearchItem[]): void {
    if (this._user != null) {
      this._user.profilesViewedDuringTrialItems = profilesItems;
    }
  }

  @Mutation
  public setHidePayWalledProfilesPreference(value: boolean): void {
    if (this._user != null) {
      this._user.hidePayWalledProfilesPreference = value;
    } else {
      LogService.warn(`SessionModule.setHidePayWalledProfilesPreference is called before user is initialized.`);
    }
  }

  @Mutation
  public setHasVignetteAccess(hasVignetteAccess: boolean): void {
    this._hasVignetteAccess = hasVignetteAccess;
  }

  @Mutation
  public setVignetteUserManagement(isVignetteUserManager: boolean): void {
    if (this._user != null) {
      this._user.isVignetteUserManager = isVignetteUserManager;
    } else {
      LogService.warn(`SessionModule.setVignetteUserManagement is called before user is initialized.`);
    }
  }

  @Mutation
  public refreshViewedProfilesBriefcaseStatus(): void {
    if (this._user != null) {
      this._user.profilesViewedDuringTrialItems.forEach((searchItem) => {
        searchItem.addedToBriefcase = getStoreModule(ApplicationModule, store).isAddedToBriefcaseById(
          searchItem.briefcaseId
        );
      });
    }
  }

  //***** ACTIONS - ASYNCHRONOUS *****//

  // DEV REMINDERS:
  // Mutation functions MUST NOT be async functions.
  // Also do not define Mutation & Actions as arrow️ functions, since we need to rebind them at runtime.

  @Action
  public async login(): Promise<void> {
    // USed in navbar Login button
    // TODO: is this needed? use if needed to update after session endpoint
    window.location.href = getStoreModule(ApplicationModule, store).getBaseUrlOrigin + VignetteEndpoints.LOGIN;
  }

  @Action
  public async logout(): Promise<void> {
    // Used in navbar Logout button
    this.setUser(null);

    window.location.href = getStoreModule(ApplicationModule, store).getBaseUrlOrigin + VignetteEndpoints.LOGOUT;
  }
}
